/**
 * src/lib/api/knowledge.js — Brain/Corpus API operations
 *
 * Wraps all brain.* actions from /api/data for Knowledge components.
 * All calls route through apiFetch for auto-tracing (Phase 78).
 */

import { apiFetch, API_BASE } from './client';
import { supabase } from '../supabase';
import { CHAT_CHIP_DECISIONS } from '../agent/brainChips';

/**
 * TICKET-1499: a chat-proposed brain chip is confirmed or discarded by the person in the
 * chat (confirm -> 'accepted', discard -> 'rejected', src/lib/agent/brainChips.js). The
 * UPDATE trigger re-indexes Chroma/LightRAG; quote reads exclude 'rejected'.
 */
export async function decideBrainChip(chipId, decision) {
  const status = CHAT_CHIP_DECISIONS[decision];
  if (!chipId || !status) throw new Error(`decideBrainChip: bad chip/decision (${decision})`);
  const { data: { user } = {} } = await supabase.auth.getUser();
  const { error } = await supabase.from('vessel_brain_entries')
    .update({ status, decided_by: user?.id || null, decided_at: new Date().toISOString() })
    .eq('id', chipId);
  if (error) throw new Error(error.message);
  return { chip_id: chipId, status };
}

/** TICKET-1499: current status of chat chips, read back so a reload never re-asks. { id: status } */
export async function fetchBrainChipStatuses(chipIds) {
  const ids = (chipIds || []).filter(Boolean);
  if (!ids.length) return {};
  const { data, error } = await supabase.from('vessel_brain_entries').select('id, status').in('id', ids);
  if (error) throw new Error(error.message);
  return Object.fromEntries((data || []).map((r) => [r.id, r.status]));
}

export async function fetchBrainQueue(status = 'pending') {
  return apiFetch(`${API_BASE}/data?action=brain.queue&status=${status}`);
}

export async function approveBrainItem(queueId) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'brain.approve', queue_id: queueId }),
  });
}

export async function rejectBrainItem(queueId) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'brain.reject', queue_id: queueId }),
  });
}

export async function fetchBrainDocuments(limit = 50) {
  return apiFetch(`${API_BASE}/data?action=brain.documents&limit=${limit}`);
}

export async function saveBrainUploadResult(payload) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'brain.upload-result', ...payload }),
  });
}

/**
 * Phase 5 (TICKET-068) — generate a brain-grounded document.
 * payload: { doc_type:'quote'|'design_criteria', product_type, project_id?,
 *            deal_id?, vessel_id?, dimensions?, notes?, client_name?, boat_name?, language? }
 * Routes to /api/ai (role derived server-side from the JWT; pricing gated there).
 */
export async function generateBrainContent(payload) {
  return apiFetch(`${API_BASE}/ai`, {
    method: 'POST',
    body: JSON.stringify({ action: 'brain.generate_content', ...payload }),
    // Brain-grounded generation runs cross-project reference + BOM inference +
    // summarize + an LLM call server-side (~30-40s observed). CEO bug 2026-06-19:
    // the 15s apiFetch default aborted CLIENT-side while the server kept running
    // and logged `[brain.generate_content] ok` AFTER the client gave up
    // (action_result: timeout). 120s matches quotes.scope_generate.
    timeout: 120000,
  });
}

/**
 * Phase 77-02 — persist an audio-upload transcript to the brain:
 * LightRAG insert (quality-scored) + project_documents row.
 *
 * @param {object} payload
 * @param {string} payload.recording_id
 * @param {string} payload.file_name
 * @param {number} [payload.file_size]
 * @param {string} [payload.mime_type]
 * @param {string} payload.raw_transcript
 * @param {string} payload.canonical_transcript
 * @param {Array<{from,to,entity_type,entity_id,confidence}>} [payload.replacements]
 * @param {string} [payload.project_id]
 * @param {string} [payload.title]
 * @returns {Promise<{ success: boolean, document_id?: string, lightrag_route?: string, quality_score?: number }>}
 */
export async function saveAudioTranscript(payload) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'brain.save-audio-transcript', ...payload }),
  });
}

export async function fetchBrainStats() {
  return apiFetch(`${API_BASE}/data?action=brain.stats`);
}

/**
 * TICKET-114 B0 — manually run the doc→brain extraction drain (the cron analog).
 * Converts uploaded documents that carry extracted_text into vessel_brain_entries
 * 'proposed' chips on demand. CEO/admin gated server-side (routeBrain).
 * @param {number} [limit=10] capped at 25 server-side.
 * @returns {Promise<{ success: boolean, drained?: number, results?: Array, error?: string }>}
 */
export async function drainDocumentsToBrain(limit = 10) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'brain.drain_documents', limit }),
  });
}

/**
 * Upload an audio file to the brain by chunking it client-side and routing
 * each chunk through the existing voice.upload_chunk handler, then invoking
 * voice.finalize_recording to stitch + STT + canonicalize.
 *
 * Phase 77-02: finalize_recording returns { text: canonical, raw_text, replacements[] }.
 *   The `transcript` field in the return value is the canonical text (backwards-compat);
 *   raw_transcript + replacements are additive.
 * Phase 77-03: finalize now streams via SSE (`stream: true`). Client reads
 *   event-stream body via ReadableStream and emits per-stage onChunkProgress.
 *   Falls back to the JSON wait_for_transcript path when the response is not
 *   text/event-stream (old server, proxy stripped header, or ReadableStream
 *   unavailable).
 *
 * onChunkProgress stages:
 *   - 'upload'       — per-chunk upload (77-01)
 *   - 'stitching'    — server downloaded chunks, about to run STT (77-03)
 *   - 'transcribing' — server STT batch complete, { uploaded, total } are real counts (77-03)
 *   - 'normalizing'  — server about to run canonical pass (77-03)
 *
 * @param {File|Blob} file — uploaded audio file
 * @param {object} opts
 * @param {Function} [opts.onChunkProgress] — fires { uploaded, total, stage }
 * @param {Function} [opts.chunker] — injectable chunker for tests (default: audioFileChunker.chunk)
 * @returns {Promise<{ recording_id: string, transcript: string, raw_transcript: string, replacements: Array }>}
 */
export async function uploadAudioFile(file, opts = {}) {
  const chunker = opts.chunker || (await import('../voice/audioFileChunker.js')).chunk;
  const onChunkProgress = opts.onChunkProgress || (() => {});

  const chunks = await chunker(file);
  const total = chunks.length;
  const recording_id = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `audio-upload-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  onChunkProgress({ uploaded: 0, total, stage: 'upload' });

  for (let i = 0; i < total; i++) {
    const { blob } = chunks[i];
    const audio = await blobToBase64(blob);
    const isFinal = i === total - 1;
    const result = await apiFetch(`${API_BASE}/ai`, {
      method: 'POST',
      body: JSON.stringify({
        action: 'voice.upload_chunk',
        recording_id,
        chunk_index: i,
        audio,
        mime_type: 'audio/wav',
        is_final_chunk: isFinal,
        source: 'file_upload',
        language: 'auto',
      }),
    });
    if (result && result.success === false) {
      throw new Error(result.error || `Chunk ${i} upload failed`);
    }
    onChunkProgress({ uploaded: i + 1, total, stage: 'upload' });
  }

  // Phase 77-03: flip the UI into "server working" state immediately after
  // the last upload so the CEO sees motion while the server boots STT.
  // The server will overwrite this with its own `stitching` event moments later.
  // Note: replaces the Phase 77-01 `{ stage: 'transcribing' }` transitional event —
  // 'transcribing' now carries server-driven counts only (77-03 AC-3).
  onChunkProgress({ uploaded: 0, total, stage: 'stitching' });

  return await finalizeViaSse(recording_id, onChunkProgress);
}

/**
 * Phase 77-03 — finalize via SSE with graceful fallback to the 77-02 JSON contract.
 */
async function finalizeViaSse(recording_id, onChunkProgress) {
  let finalizeResp;
  try {
    finalizeResp = await fetch(`${API_BASE}/ai`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      credentials: 'include',
      body: JSON.stringify({
        action: 'voice.finalize_recording',
        recording_id,
        stream: true,
        source: 'file_upload',
      }),
    });
  } catch {
    // Network-level failure on the streaming fetch — try JSON path.
    return await finalizeViaJson(recording_id);
  }

  if (!finalizeResp.ok || !finalizeResp.body) {
    // Old server without SSE support or environment lacking ReadableStream.
    return await finalizeViaJson(recording_id);
  }

  const contentType = finalizeResp.headers.get('content-type') || '';
  if (!contentType.includes('text/event-stream')) {
    // Server ignored the stream flag or proxy stripped the header.
    // Treat the response body as JSON per the 77-02 contract.
    let json;
    try { json = await finalizeResp.json(); } catch {
      throw new Error('Finalize failed: non-SSE response without parsable JSON');
    }
    if (json?.success === false) throw new Error(json.error || 'Finalize failed');
    return {
      recording_id,
      transcript: json?.text || '',
      raw_transcript: json?.raw_text || json?.text || '',
      replacements: Array.isArray(json?.replacements) ? json.replacements : [],
    };
  }

  const done = await parseSseStream(finalizeResp, { onProgress: onChunkProgress });
  return {
    recording_id,
    transcript: done?.text || '',
    raw_transcript: done?.raw_text || done?.text || '',
    replacements: Array.isArray(done?.replacements) ? done.replacements : [],
  };
}

/**
 * JSON fallback — called when SSE is unavailable. Preserves the Phase 77-02
 * wait_for_transcript contract byte-for-byte.
 */
async function finalizeViaJson(recording_id) {
  const finalResult = await apiFetch(`${API_BASE}/ai`, {
    method: 'POST',
    body: JSON.stringify({
      action: 'voice.finalize_recording',
      recording_id,
      wait_for_transcript: true,
      source: 'file_upload',
    }),
  });
  if (finalResult && finalResult.success === false) {
    throw new Error(finalResult.error || 'Finalize failed');
  }
  return {
    recording_id,
    transcript: finalResult?.text || '',
    raw_transcript: finalResult?.raw_text || finalResult?.text || '',
    replacements: Array.isArray(finalResult?.replacements) ? finalResult.replacements : [],
  };
}

/**
 * Minimal SSE frame parser over a fetch Response body. Resolves with the `done`
 * event payload, throws on `error` event or if the stream ends before `done`.
 */
async function parseSseStream(response, { onProgress } = {}) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let doneEvent = null;
  let errorEvent = null;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let frameEnd;
    while ((frameEnd = buffer.indexOf('\n\n')) >= 0) {
      const frame = buffer.slice(0, frameEnd);
      buffer = buffer.slice(frameEnd + 2);
      const lines = frame.split('\n');
      let eventName = 'message';
      let dataStr = '';
      for (const ln of lines) {
        if (ln.startsWith('event:')) eventName = ln.slice(6).trim();
        else if (ln.startsWith('data:')) dataStr += ln.slice(5).trim();
      }
      if (!dataStr) continue;
      let payload;
      try { payload = JSON.parse(dataStr); } catch { continue; }
      if (eventName === 'progress') { if (onProgress) onProgress(payload); }
      else if (eventName === 'done')  { doneEvent = payload; }
      else if (eventName === 'error') { errorEvent = payload; }
    }
  }
  if (errorEvent) throw new Error(errorEvent.message || 'Streaming finalize failed');
  if (!doneEvent) throw new Error('Streaming finalize ended without a done event');
  return doneEvent;
}

/** Convert a Blob to a base64 string (no data: prefix). */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Failed to read audio blob'));
    reader.readAsDataURL(blob);
  });
}
